import axios from 'axios'
import fp from 'lodash/fp'
import log from 'utils/logger'
import { ERRORS } from 'constants/error.messages'
import { convertBlobToJson } from 'utils/standard/binary'

export default {
  setup(appStore, { authService }) {
    let originalError = null

    axios.interceptors.request.use(async (config) => {
      const { auth } = appStore

      if (
        auth.token?.isAboutToExpire &&
        !authService.isTokenRefreshUrl(config.url)
      ) {
        try {
          await auth.tryRefreshToken()
        } catch (error) {
          // If refresh fails, the token will be cleared in tryRefreshToken
          // Continue with the request without the token
        }
      }

      if (auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`
      }

      return config
    })

    axios.interceptors.response.use(fp.identity, async (err) => {
      log.error(err)

      const originalRequest = err.config

      if (originalRequest.sandbox) {
        return err.response
      }

      const { token } = appStore.auth
      let responseData = fp.getOr(err, 'response.data', err)

      if (responseData?.constructor === Blob) {
        responseData = await convertBlobToJson(responseData)
      }

      const syntheticError = {
        message: ERRORS.UNKNOWN,
        ...responseData,
        config: err.config,
      }

      // Handle specific error codes with custom messages
      if (responseData?.errorCode === 'INVALID_CREDENTIALS') {
        syntheticError.message = ERRORS.INVALID_CREDENTIALS
      }
      const needRetry =
        token?.isExpired &&
        !authService.isTokenRefreshUrl(originalRequest.url) &&
        !originalRequest._retried

      if (needRetry) {
        originalError = syntheticError
        try {
          const isSuccessfullyRefreshed = await appStore.auth.tryRefreshToken()

          if (!isSuccessfullyRefreshed) {
            originalError.message = ERRORS.TOKEN_EXPIRED
            appStore.auth.signout()
            throw originalError
          }

          const { token } = appStore.auth

          originalRequest._retried = true
          originalRequest.headers.Authorization = `Bearer ${token}`

          return axios(originalRequest)
        } catch (refreshError) {
          // If refresh token fails, sign out but don't reject the promise yet
          originalError.message = ERRORS.TOKEN_EXPIRED
          appStore.auth.signout()
          throw originalError
        }
      }

      if (
        authService.isTokenRefreshUrl(originalRequest.url) ||
        originalRequest._retried
      ) {
        syntheticError.message = ERRORS.TOKEN_EXPIRED
        appStore.auth.signout()

        throw originalError || syntheticError
      }

      throw syntheticError
    })
  },
}
